/**
 * L'AVION: il vole tout seul, et c'est ce qui le distingue des deux autres.
 *
 * Une voiture et une barque SUIVENT leur pilote: le joueur pousse le joystick,
 * le bonhomme avance, le vehicule est ramene sous lui (`majVehicule`). Un avion
 * ne peut pas marcher comme ca — un appareil ne s'arrete pas en l'air et ne
 * tourne pas sur place. Ici, c'est l'inverse: L'AVION AVANCE, ET LE PILOTE EST
 * ASSIS DEDANS. Le joueur ne pousse rien; il DESIGNE un endroit, et l'appareil
 * y va, puis tourne en rond au-dessus jusqu'a ce qu'on lui dise autre chose.
 *
 * CE QU'ON LUI DEMANDE (la commande du proprietaire, mot pour mot):
 *
 *   « il va toujours, sauf au sol. On clique quelque part, l'avion y va, puis
 *     fait des orbites circulaires, jusqu'a ce qu'on clique ailleurs ou qu'on
 *     clique un aeroport, et alors il s'aligne proprement et se pose. »
 *
 * D'ou la machine a etats ci-dessous. Elle est PURE: pas de DOM, pas d'horloge,
 * pas de hasard, aucune dependance a three.js. Elle tourne donc a l'identique
 * dans le navigateur et dans un test, ce qui est la seule facon de verifier
 * qu'un atterrissage est aligne — on ne juge pas un alignement a l'oeil sur une
 * capture, on le MESURE (voir tests/unit/avion.test.ts).
 *
 * L'ALIGNEMENT, justement, est le morceau delicat, et il est traite comme dans
 * la vraie vie plutot qu'en tricheur:
 *
 *   - on rejoint d'abord un point d'attente dans l'AXE de la piste, loin devant
 *     le seuil (`LONGUEUR_FINALE`);
 *   - on y tourne en rond en descendant et en ralentissant, jusqu'a se
 *     retrouver, tangentiellement, cap sur la piste: c'est la PORTE D'ENTREE en
 *     finale, et rien ne la force — on attend le passage;
 *   - en finale, on suit l'axe avec un point de mire glissant (`pointDeMire`)
 *     qui rattrape le decalage lateral, on descend proportionnellement a ce
 *     qui reste, et l'on touche au seuil, aligne.
 *
 * Aucun demi-tour sur place, aucune teleportation, aucun « snap » sur l'axe:
 * tout ce que fait l'avion, il le fait en volant.
 */
import { SEA_LEVEL, WALK_SPEED } from '../constants.ts'
import type { Vehicule } from '../types/monde.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import { clamp } from './alea.ts'
import { RECUL_PARKING, pointPiste, reperePiste, type Piste } from './batiments.ts'

// ------------------------------------------------------------------ reglages

/** Vitesse de croisiere: CINQ FOIS la marche, comme demande. */
export const V_CROISIERE = 5 * WALK_SPEED

/** Vitesse a laquelle l'avion quitte le sol, et vitesse d'approche, en m/s. */
export const V_ENVOL = 9

/** Acceleration et freinage au sol, en m/s². */
export const ACCEL_SOL = 5
export const FREIN_SOL = 5

/** Acceleration en l'air (et deceleration a l'approche), en m/s². */
export const ACCEL_AIR = 3

/** Altitude de croisiere au-dessus du SOL, en metres. */
export const ALTITUDE_CROISIERE = 40

/** Altitude a laquelle on aborde la finale, en metres. */
export const ALTITUDE_FINALE = 12

/** Vitesse de montee et de descente hors finale, en m/s. */
export const TAUX_MONTEE = 5

/**
 * Rayon de virage, en metres. Il donne la vitesse angulaire maximale
 * (`vitesse / RAYON_VIRAGE`): a 20 m/s, l'avion met une dizaine de secondes a
 * faire le tour, ce qui se lit comme un avion et non comme une mouche.
 */
export const RAYON_VIRAGE = 26

/** Rayon des orbites au-dessus du point designe, en metres. */
export const RAYON_ORBITE = 32

/** Longueur de la finale, du point d'attente au seuil, en metres. */
export const LONGUEUR_FINALE = 110

/** Distance a laquelle on considere le point d'attente atteint, en metres. */
export const CAPTURE_ATTENTE = 40

/** Ecart de cap tolere pour entrer en finale, en radians (~17 degres). */
export const TOLERANCE_ALIGNEMENT = 0.3

/** Ecart lateral tolere pour entrer en finale, en metres. */
export const ECART_LATERAL_MAX = 45

/**
 * LA HAUTEUR DE VOL NE COPIE PAS LE RELIEF — sinon l'appareil TREMBLE.
 *
 * `altitude` est une hauteur AU-DESSUS DU SOL, et c'est ce qu'il faut pour
 * decoller, se poser et ne jamais traverser une montagne. Mais poser bêtement
 * l'appareil a « hauteur du terrain + altitude » lui fait recopier chaque
 * bosse: mesure faite sur le vrai monde, a vingt metres par seconde et un
 * metre par tick, le sol change de 12 a 18 cm par tick en moyenne et jusqu'a
 * 2,9 m d'un seul coup au bord d'une falaise — c'est-a-dire 58 m/s de
 * montee-descente. A quarante metres du sol, ca ne se lit pas comme du relief:
 * ca se lit comme un avion qui vibre.
 *
 * On vole donc au-dessus d'un SOL DE REFERENCE lisse (`e.solLisse`, un premier
 * ordre de constante de temps ~1,7 s, soit 34 m parcourus), et l'on revient au
 * terrain EXACT a mesure qu'on descend (`melangeSol`): en finale et au
 * roulage, ce qui compte est la piste sous les roues, pas une moyenne. Le
 * plancher `GARDE_SOL` interdit au lissage de faire piquer l'appareil dans une
 * butte qui monte plus vite que lui.
 */
export const K_SOL_LISSE = 0.6

/** Au-dessus de cette hauteur, on vole entierement sur le sol lisse. */
export const HAUTEUR_LISSAGE = ALTITUDE_FINALE

/** Hauteur minimale au-dessus du terrain reel tant qu'on est en l'air. */
export const GARDE_SOL = 1.2

// ------------------------------------------------------------------- etat

/**
 * Les phases du vol. Elles se suivent dans cet ordre et ne sautent jamais:
 * `gare` → `decollage` → `montee` → `croisiere` → `orbite`, et pour se poser
 * `transit` → `alignement` → `finale` → `roulage` → `gare`.
 */
export type PhaseAvion =
  | 'gare'
  | 'decollage'
  | 'montee'
  | 'croisiere'
  | 'orbite'
  | 'transit'
  | 'alignement'
  | 'finale'
  | 'roulage'

export interface EtatAvion {
  phase: PhaseAvion
  /** Aeroport ou l'appareil est pose (ou d'ou il vient de partir). */
  base: string | null
  /** Aeroport vise pour se poser, sinon null. */
  destination: string | null
  /** Point designe par le joueur: on y va, puis on tourne autour. */
  cible: Vec2 | null
  /** Hauteur au-dessus du SOL, en metres. Zero quand les roues touchent. */
  altitude: number
  /** Vitesse courante, en m/s. */
  vitesse: number
  /** Sens des orbites: +1 ou -1. Choisi au premier tour, gardé ensuite. */
  sensOrbite: 1 | -1
  /**
   * Sol de reference, lisse: la hauteur au-dessus de laquelle on vole.
   * `null` tant qu'on n'a pas vole (au sol, c'est le terrain qui commande).
   */
  solLisse: number | null
}

/** Un appareil neuf, gare a son aeroport, moteur coupe. */
export function etatAvionNeuf(base: string | null): EtatAvion {
  return {
    phase: 'gare',
    base,
    destination: null,
    cible: null,
    altitude: 0,
    vitesse: 0,
    sensOrbite: 1,
    solLisse: null,
  }
}

/** Vrai si les roues touchent le sol: c'est la SEULE facon de ne pas avancer. */
export function auSol(e: EtatAvion): boolean {
  return e.phase === 'gare' || e.phase === 'decollage' || e.phase === 'roulage'
}

/** Vrai si l'appareil est immobile a son parking: on peut y monter, en sortir. */
export function estGare(e: EtatAvion): boolean {
  return e.phase === 'gare'
}

// ------------------------------------------------------------------ ordres

/**
 * « Va la-bas. » Depuis le parking, cela veut dire decoller d'abord: on ne se
 * teleporte pas en l'air.
 */
export function viserPoint(e: EtatAvion, p: Vec2): void {
  e.cible = { x: p.x, z: p.z }
  e.destination = null
  if (e.phase === 'gare') e.phase = 'decollage'
  else if (e.phase === 'roulage') return
  else if (e.phase !== 'decollage' && e.phase !== 'montee') e.phase = 'croisiere'
}

/**
 * « Pose-toi la. » Meme regle: au sol on decolle d'abord — un avion gare a un
 * aeroport et envoye vers un autre fait le voyage.
 */
export function viserAeroport(e: EtatAvion, id: string, centre: Vec2): void {
  e.destination = id
  e.cible = { x: centre.x, z: centre.z }
  if (e.phase === 'gare') e.phase = 'decollage'
  else if (e.phase !== 'decollage' && e.phase !== 'montee' && e.phase !== 'roulage') e.phase = 'transit'
}

// -------------------------------------------------------------- geometrie

/** Cap (radians) du point `de` vers le point `vers`. 0 = vers +z. */
export function capVers(de: Vec2, vers: Vec2): number {
  return Math.atan2(vers.x - de.x, vers.z - de.z)
}

/** Ecart d'angle ramene dans [-PI, PI]. */
export function ecartAngle(a: number, b: number): number {
  let d = (a - b) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d < -Math.PI) d += Math.PI * 2
  return d
}

/**
 * Tourne vers le cap voulu, sans depasser ce qu'un avion peut faire.
 *
 * La vitesse angulaire vient du rayon de virage: `v / R`. Un plancher evite
 * qu'un appareil presque arrete (juste apres la rotation) ne puisse plus
 * tourner du tout.
 */
export function virer(capActuel: number, capVoulu: number, vitesse: number, dt: number): number {
  const omega = Math.max(0.25, vitesse / RAYON_VIRAGE)
  const d = ecartAngle(capVoulu, capActuel)
  return capActuel + clamp(d, -omega * dt, omega * dt)
}

/**
 * LE POINT DE MIRE de la finale: un point sur l'axe, devant l'appareil.
 *
 * Viser l'axe « au plus court » ferait piquer vers lui puis osciller autour;
 * viser un point qui GLISSE devant soi rattrape l'ecart en douceur et se
 * stabilise — c'est la poursuite pure des vrais pilotes automatiques. Le
 * regard porte loin quand il reste du chemin et se raccourcit en approchant du
 * seuil, ce qui resserre la trajectoire la ou ca compte.
 */
export function pointDeMire(piste: Piste, x: number, z: number): Vec2 {
  const r = reperePiste(piste, x, z)
  const restant = Math.max(0, r.long - piste.longueur)
  const regard = clamp(restant * 0.6, 14, 45)
  return pointPiste(piste, r.long - regard, 0)
}

// ------------------------------------------------------------------- pas

/** Ce dont un pas de vol a besoin: les pistes concernees, resolues par l'appelant. */
export interface ContexteAvion {
  /** Piste de l'aeroport ou l'appareil est gare (sert au decollage). */
  pisteBase: Piste | null
  /** Piste visee pour se poser. */
  pisteDestination: Piste | null
  /**
   * Ou s'arreter apres l'atterrissage, en metres depuis le seuil proche.
   *
   * L'appelant le choisit parce que lui seul connait les AUTRES appareils: se
   * garer toujours au meme metre finirait par empiler deux avions au meme
   * endroit. Absent = le point de stationnement ordinaire.
   */
  arret?: number
}

/** Avance l'appareil de `d` metres suivant son cap. */
function avancer(v: Vehicule, d: number): void {
  v.pos.x += Math.sin(v.rot) * d
  v.pos.z += Math.cos(v.rot) * d
}

/**
 * UN PAS DE VOL. Mute le vehicule (position, cap, altitude) et l'etat.
 *
 * Le contrat, tenu par tests/unit/avion.test.ts:
 *  - en l'air, la vitesse ne descend JAMAIS sous `V_ENVOL`: ca vole ou ca roule;
 *  - le cap ne saute jamais de plus de ce que `virer` autorise;
 *  - l'appareil ne touche le sol que sur une piste, aligne avec elle;
 *  - il ne s'immobilise qu'au parking.
 */
export function majAvion(v: Vehicule, e: EtatAvion, dt: number, terrain: ITerrain, ctx: ContexteAvion): void {
  switch (e.phase) {
    case 'gare':
      e.vitesse = 0
      e.altitude = 0
      break

    case 'decollage':
      pasDecollage(v, e, dt, ctx)
      break

    case 'montee':
      pasMontee(v, e, dt)
      break

    case 'croisiere':
      pasCroisiere(v, e, dt)
      break

    case 'orbite':
      pasOrbite(v, e, dt)
      break

    case 'transit':
      pasTransit(v, e, dt, ctx)
      break

    case 'alignement':
      pasAlignement(v, e, dt, ctx)
      break

    case 'finale':
      pasFinale(v, e, dt, ctx)
      break

    case 'roulage':
      pasRoulage(v, e, dt, ctx)
      break
  }

  v.y = hauteurDeVol(v.pos.x, v.pos.z, e, dt, terrain)
}

/**
 * LE SOL, VU D'UN AVION: jamais sous la mer.
 *
 * `altitude` se compte au-dessus du sol, et entre les deux mondes le sol est le
 * FOND MARIN, quinze metres sous la surface. Sans ce plafond, l'appareil
 * descendrait de vingt-cinq metres en quittant le rivage puis remonterait
 * d'autant en arrivant a l'autre: une seule vague de sept cents metres de long,
 * qu'aucun pilote n'a demandee. Un avion n'a jamais eu a franchir un fond
 * marin — ce qu'il doit survoler, en mer, c'est l'eau.
 *
 * C'est la meme famille que « une hauteur relative au sol RECOPIE le sol »,
 * deja payee avec le tremblement au-dessus des falaises.
 */
function solDeVol(terrain: ITerrain, x: number, z: number): number {
  return Math.max(terrain.hauteur(x, z), SEA_LEVEL)
}

/**
 * LA HAUTEUR A LAQUELLE ON DESSINE L'APPAREIL, sol de reference compris.
 *
 * Au sol, c'est le terrain, sans un gramme de lissage: les roues touchent la
 * piste. En l'air, c'est le sol de reference lisse plus l'altitude, avec un
 * retour progressif vers le terrain exact quand on descend — et un plancher,
 * pour qu'une butte qui monte plus vite que le filtre ne traverse jamais la
 * carlingue.
 */
function hauteurDeVol(x: number, z: number, e: EtatAvion, dt: number, terrain: ITerrain): number {
  const sol = solDeVol(terrain, x, z)
  if (auSol(e)) {
    e.solLisse = sol
    return sol + e.altitude
  }
  if (e.solLisse === null) e.solLisse = sol
  else e.solLisse += (sol - e.solLisse) * (1 - Math.exp(-K_SOL_LISSE * dt))
  const melange = clamp(e.altitude / HAUTEUR_LISSAGE, 0, 1)
  const reference = sol + (e.solLisse - sol) * melange
  return Math.max(reference + e.altitude, sol + Math.min(e.altitude, GARDE_SOL))
}

/** La course au sol, cap bloque sur l'axe: on ne se dirige pas, on accelere. */
function pasDecollage(v: Vehicule, e: EtatAvion, dt: number, ctx: ContexteAvion): void {
  const piste = ctx.pisteBase
  if (!piste) {
    // Sans piste (aeroport demoli sous les roues), on reste au sol plutot que
    // de partir de travers: c'est la seule reponse honnete.
    e.vitesse = 0
    return
  }
  v.rot = piste.cap
  e.altitude = 0
  e.vitesse = Math.min(V_CROISIERE, e.vitesse + ACCEL_SOL * dt)
  avancer(v, e.vitesse * dt)
  if (e.vitesse >= V_ENVOL) e.phase = 'montee'
}

/** On quitte le sol, on monte, et on commence a se diriger. */
function pasMontee(v: Vehicule, e: EtatAvion, dt: number): void {
  e.vitesse = Math.min(V_CROISIERE, e.vitesse + ACCEL_AIR * dt)
  e.altitude = Math.min(ALTITUDE_CROISIERE, e.altitude + TAUX_MONTEE * dt)
  if (e.cible) v.rot = virer(v.rot, capVers(v.pos, e.cible), e.vitesse, dt)
  avancer(v, e.vitesse * dt)
  if (e.altitude >= ALTITUDE_CROISIERE) e.phase = e.destination ? 'transit' : 'croisiere'
}

/** Vers le point designe, a la vitesse et a l'altitude de croisiere. */
function pasCroisiere(v: Vehicule, e: EtatAvion, dt: number): void {
  regimeDeCroisiere(e, dt)
  if (!e.cible) {
    avancer(v, e.vitesse * dt)
    return
  }
  v.rot = virer(v.rot, capVers(v.pos, e.cible), e.vitesse, dt)
  avancer(v, e.vitesse * dt)
  const d = Math.hypot(e.cible.x - v.pos.x, e.cible.z - v.pos.z)
  if (d <= RAYON_ORBITE) {
    // Le sens de l'orbite est celui vers lequel l'appareil penche deja: il
    // enroule sa trajectoire au lieu de la croiser.
    e.sensOrbite = ecartAngle(capVers(v.pos, e.cible), v.rot) >= 0 ? 1 : -1
    e.phase = 'orbite'
  }
}

/**
 * LES RONDS AU-DESSUS DU POINT DESIGNE.
 *
 * On ne calcule pas un cercle: on vise un point de la circonference un peu plus
 * loin sur le tour (une « carotte »), et l'appareil l'enroule tout seul. Le
 * rayon se corrige donc de lui-meme, sans jamais forcer la position.
 */
function pasOrbite(v: Vehicule, e: EtatAvion, dt: number): void {
  regimeDeCroisiere(e, dt)
  const c = e.cible
  if (!c) {
    avancer(v, e.vitesse * dt)
    return
  }
  const angle = Math.atan2(v.pos.x - c.x, v.pos.z - c.z)
  const suivant = angle + e.sensOrbite * 0.55
  const carotte = { x: c.x + Math.sin(suivant) * RAYON_ORBITE, z: c.z + Math.cos(suivant) * RAYON_ORBITE }
  v.rot = virer(v.rot, capVers(v.pos, carotte), e.vitesse, dt)
  avancer(v, e.vitesse * dt)
}

/** Croisiere: on tend vers la vitesse et l'altitude de croisiere. */
function regimeDeCroisiere(e: EtatAvion, dt: number): void {
  e.vitesse = clamp(e.vitesse + ACCEL_AIR * dt, V_ENVOL, V_CROISIERE)
  e.altitude += clamp(ALTITUDE_CROISIERE - e.altitude, -TAUX_MONTEE * dt, TAUX_MONTEE * dt)
}

/** Le voyage vers le point d'attente de la piste visee. */
function pasTransit(v: Vehicule, e: EtatAvion, dt: number, ctx: ContexteAvion): void {
  const piste = ctx.pisteDestination
  if (!piste) {
    // L'aeroport a disparu (demoli): on ne s'obstine pas, on tourne en rond.
    e.destination = null
    e.phase = 'croisiere'
    pasCroisiere(v, e, dt)
    return
  }
  const attente = pointPiste(piste, piste.longueur + LONGUEUR_FINALE, 0)
  e.cible = attente
  regimeDeCroisiere(e, dt)
  v.rot = virer(v.rot, capVers(v.pos, attente), e.vitesse, dt)
  avancer(v, e.vitesse * dt)
  if (Math.hypot(attente.x - v.pos.x, attente.z - v.pos.z) <= CAPTURE_ATTENTE) {
    e.sensOrbite = ecartAngle(capVers(v.pos, attente), v.rot) >= 0 ? 1 : -1
    e.phase = 'alignement'
  }
}

/**
 * LE TOUR D'ATTENTE, dans l'axe de la piste.
 *
 * On tourne autour du point d'attente en descendant et en ralentissant, et l'on
 * ne part en finale QUE lorsque le tour nous presente cap sur la piste. C'est
 * ce qui remplace le demi-tour sur place: l'avion prend son temps, comme un
 * vrai, et se trouve aligne parce qu'il a attendu de l'etre.
 */
function pasAlignement(v: Vehicule, e: EtatAvion, dt: number, ctx: ContexteAvion): void {
  const piste = ctx.pisteDestination
  if (!piste) {
    e.destination = null
    e.phase = 'croisiere'
    return
  }
  const attente = pointPiste(piste, piste.longueur + LONGUEUR_FINALE, 0)
  e.cible = attente
  // On ralentit a la vitesse d'approche et l'on descend a l'altitude de finale:
  // arriver vite et haut obligerait a plonger, ce qui ne ressemble a rien.
  e.vitesse = clamp(e.vitesse - ACCEL_AIR * dt, V_ENVOL, V_CROISIERE)
  e.altitude += clamp(ALTITUDE_FINALE - e.altitude, -TAUX_MONTEE * dt, TAUX_MONTEE * dt)

  const angle = Math.atan2(v.pos.x - attente.x, v.pos.z - attente.z)
  const suivant = angle + e.sensOrbite * 0.55
  const carotte = { x: attente.x + Math.sin(suivant) * RAYON_ORBITE, z: attente.z + Math.cos(suivant) * RAYON_ORBITE }
  v.rot = virer(v.rot, capVers(v.pos, carotte), e.vitesse, dt)
  avancer(v, e.vitesse * dt)

  if (porteDeFinale(v, e, piste)) e.phase = 'finale'
}

/**
 * LA PORTE D'ENTREE EN FINALE: quatre conditions, toutes necessaires.
 *
 * Ce sont elles qui garantissent qu'on ne se pose jamais de travers: on ne
 * force rien, on attend que l'appareil se presente bien. Le tour d'attente
 * l'amene sous ce nez-la a chaque tour.
 */
function porteDeFinale(v: Vehicule, e: EtatAvion, piste: Piste): boolean {
  const capAtterrissage = piste.cap + Math.PI
  if (Math.abs(ecartAngle(v.rot, capAtterrissage)) > TOLERANCE_ALIGNEMENT) return false
  const r = reperePiste(piste, v.pos.x, v.pos.z)
  // Au-dela du seuil lointain: on doit avoir toute la piste devant soi.
  if (r.long < piste.longueur + 20) return false
  if (Math.abs(r.lat) > ECART_LATERAL_MAX) return false
  return e.altitude <= ALTITUDE_FINALE + 3
}

/**
 * LA FINALE: on suit l'axe, on descend a proportion de ce qui reste, on touche
 * au seuil.
 *
 * L'altitude est asservie a la DISTANCE et non au temps: un appareil un peu
 * plus rapide arrive donc au seuil a la meme hauteur, jamais au-dessus.
 */
function pasFinale(v: Vehicule, e: EtatAvion, dt: number, ctx: ContexteAvion): void {
  const piste = ctx.pisteDestination
  if (!piste) {
    e.destination = null
    e.phase = 'croisiere'
    return
  }
  e.vitesse = clamp(e.vitesse - ACCEL_AIR * dt, V_ENVOL, V_CROISIERE)
  v.rot = virer(v.rot, capVers(v.pos, pointDeMire(piste, v.pos.x, v.pos.z)), e.vitesse, dt)
  avancer(v, e.vitesse * dt)

  const r = reperePiste(piste, v.pos.x, v.pos.z)
  const restant = r.long - piste.longueur
  e.altitude = clamp(restant / LONGUEUR_FINALE, 0, 1) * ALTITUDE_FINALE

  if (restant <= 0) {
    // Les roues touchent: on est sur la piste, dans son axe.
    e.altitude = 0
    e.phase = 'roulage'
    e.base = e.destination
  }
}

/**
 * APRES LE TOUCHER: on freine, puis on ROULE jusqu'au parking.
 *
 * Deux regimes, comme en vrai. Le freinage mange la vitesse d'atterrissage en
 * une dizaine de metres; ce qui reste de piste se fait au pas (`V_ROULAGE`), en
 * suivant l'axe jusqu'au tablier. Sans ce second regime, l'appareil s'arretait
 * la ou le freinage l'avait laisse — au milieu de la piste, en travers du
 * suivant.
 */
function pasRoulage(v: Vehicule, e: EtatAvion, dt: number, ctx: ContexteAvion): void {
  const piste = ctx.pisteDestination ?? ctx.pisteBase
  if (!piste) {
    e.vitesse = 0
    e.phase = 'gare'
    return
  }
  const arret = ctx.arret ?? RECUL_PARKING
  e.altitude = 0
  const r = reperePiste(piste, v.pos.x, v.pos.z)
  const restant = r.long - arret

  if (e.vitesse > V_ROULAGE) e.vitesse = Math.max(V_ROULAGE, e.vitesse - FREIN_SOL * dt)
  else e.vitesse = restant > 0.6 ? V_ROULAGE : Math.max(0, e.vitesse - FREIN_SOL * dt)

  // On roule vers le tablier en suivant l'axe: le train ne derape pas. Le cap
  // se corrige au ralenti, d'ou le plancher de vitesse passe a `virer`.
  v.rot = virer(v.rot, capVers(v.pos, pointPiste(piste, arret - 2, 0)), Math.max(e.vitesse, 6), dt)
  avancer(v, e.vitesse * dt)

  if (restant <= 0.6) {
    // Gare: on se range exactement au point de stationnement, nez dans l'axe du
    // decollage — pret a repartir sans manoeuvre.
    const p = pointPiste(piste, arret, 0)
    v.pos.x = p.x
    v.pos.z = p.z
    v.rot = piste.cap
    e.vitesse = 0
    e.altitude = 0
    e.phase = 'gare'
    e.destination = null
    e.cible = null
  }
}

/** Vitesse de roulage au sol apres le freinage, en m/s. */
export const V_ROULAGE = 4
